import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { SEOHead } from '@/components/SEOHead';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Loader2, CheckCircle } from 'lucide-react';
import logo from '@/assets/gaius-logo.webp';

const passwordSchema = z.object({
  password: z.string()
    .min(8, "Le mot de passe doit contenir au moins 8 caractères")
    .regex(/[A-Z]/, "Le mot de passe doit contenir au moins une majuscule")
    .regex(/[a-z]/, "Le mot de passe doit contenir au moins une minuscule")
    .regex(/[0-9]/, "Le mot de passe doit contenir au moins un chiffre"),
  confirmPassword: z.string(),
}).refine((d) => d.password === d.confirmPassword, {
  message: "Les mots de passe ne correspondent pas",
  path: ["confirmPassword"],
});

export const ResetPasswordPage = () => {
  const { language } = useLanguage();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);
  const [isRecovery, setIsRecovery] = useState(false);
  const [tokenError, setTokenError] = useState(false);

  const fr = language === 'fr';

  useEffect(() => {
    // Check for recovery token in URL hash or query params
    const hash = window.location.hash;
    const params = new URLSearchParams(window.location.search);
    
    if (hash.includes('type=recovery') || params.get('token')) {
      setIsRecovery(true);
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsRecovery(true);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    try {
      passwordSchema.parse({ password, confirmPassword });
    } catch (err) {
      if (err instanceof z.ZodError) {
        const fieldErrors: Record<string, string> = {};
        err.errors.forEach((error) => {
          if (error.path[0]) fieldErrors[error.path[0] as string] = error.message;
        });
        setErrors(fieldErrors);
        return;
      }
    }

    setIsSubmitting(true);
    const { error } = await supabase.auth.updateUser({ password });
    setIsSubmitting(false);

    if (error) {
      if (error.message.includes('expired') || error.message.includes('invalid')) {
        setTokenError(true);
      }
      toast({ variant: 'destructive', title: fr ? 'Erreur' : 'Error', description: error.message });
    } else {
      setSuccess(true);
      setTimeout(() => navigate(fr ? '/connexion' : '/auth'), 3000);
    }
  };

  if ((!isRecovery && !success) || tokenError) {
    return (
      <div className="min-h-screen bg-secondary/30 flex items-center justify-center py-12 px-4">
        <Card className="w-full max-w-md border-border shadow-lg">
          <CardContent className="text-center py-12 space-y-4">
            <p className="text-foreground font-medium">
              {fr ? 'Lien invalide ou expiré' : 'Invalid or expired link'}
            </p>
            <p className="text-sm text-muted-foreground">
              {fr ? 'Ce lien de réinitialisation n\'est plus valide. Les liens expirent après 1 heure.' : 'This reset link is no longer valid. Links expire after 1 hour.'}
            </p>
            <Link to={fr ? '/mot-de-passe-oublie' : '/forgot-password'} className="text-primary hover:underline text-sm mt-4 inline-block">
              {fr ? 'Demander un nouveau lien' : 'Request a new link'}
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary/30 flex items-center justify-center py-12 px-4">
      <SEOHead
        title={fr ? "Nouveau mot de passe | Gaius" : "New Password | Gaius"}
        description={fr ? "Définissez votre nouveau mot de passe" : "Set your new password"}
      />

      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <Link to="/">
            <img src={logo} alt="Gaius" className="h-16 lg:h-20 mx-auto mb-4" />
          </Link>
        </div>

        <Card className="border-border shadow-lg">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold text-foreground">
              {fr ? 'Nouveau mot de passe' : 'New Password'}
            </CardTitle>
            <CardDescription>
              {fr ? 'Choisissez un nouveau mot de passe sécurisé' : 'Choose a new secure password'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {success ? (
              <div className="text-center space-y-4 py-4">
                <CheckCircle className="w-12 h-12 mx-auto text-green-500" />
                <p className="text-foreground font-medium">
                  {fr ? 'Mot de passe mis à jour !' : 'Password updated!'}
                </p>
                <p className="text-sm text-muted-foreground">
                  {fr ? 'Redirection vers la connexion...' : 'Redirecting to login...'}
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password">{fr ? 'Nouveau mot de passe' : 'New password'}</Label>
                  <Input
                    id="new-password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={errors.password ? 'border-destructive' : ''}
                  />
                  {errors.password && <p className="text-sm text-destructive">{errors.password}</p>}
                  <p className="text-xs text-muted-foreground">
                    {fr ? '8 caractères min., 1 majuscule, 1 minuscule, 1 chiffre' : 'Min 8 chars, 1 uppercase, 1 lowercase, 1 number'}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-new-password">{fr ? 'Confirmer' : 'Confirm'}</Label>
                  <Input
                    id="confirm-new-password"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={errors.confirmPassword ? 'border-destructive' : ''}
                  />
                  {errors.confirmPassword && <p className="text-sm text-destructive">{errors.confirmPassword}</p>}
                </div>
                <Button type="submit" className="w-full" disabled={isSubmitting}>
                  {isSubmitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  {fr ? 'Mettre à jour' : 'Update password'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
